import { byNameOf } from '@pubstack/common/src/sort'
import { countStacksUse, StacksBySite } from '@pubstack/common/src/stack'
import { ReactNode } from 'react'
import { Tooltip } from '~/components/Tooltip'

/** What naming a stack takes: the media's own lists build these from whole stacks, not from a usage. */
export type StackNameEntry = Pick<StacksBySite[string][number], 'stackName' | 'version' | 'enabled'>

/** The most stacks the tooltip names before counting the rest: past a handful a hover stops being read. */
const TOOLTIP_STACK_COUNT = 5

/**
 * One media's stacks, by name: the live ones, then the archived ones after them.
 *
 * Read in this order, so join it front to back — `reduce((acc, stack) => [acc, separator, stack])`.
 */
export const generateStackNameList = (stacks: StackNameEntry[]): ReactNode[] => {
  const byStackName = [...stacks].sort(byNameOf((stack) => stack.stackName))
  return [
    ...byStackName
      .filter((s) => s.enabled)
      .map((stack) => (
        <span key={stack.stackName + stack.version}>
          {stack.stackName} - {stack.version}
        </span>
      )),
    ...byStackName
      .filter((s) => !s.enabled)
      .map((stack) => (
        <span key={stack.stackName + stack.version} className={'text-silver italic'}>
          {stack.stackName} - {stack.version}
        </span>
      )),
  ]
}

type PureUsedStackTooltipProps = { stacksBySite: StacksBySite }

export const PureUsedStackTooltip = ({ stacksBySite }: PureUsedStackTooltipProps) => {
  const pipeSeparator = `\xa0 | \xa0`
  const stackCount = countStacksUse(stacksBySite)

  // The medias by name, each with its stacks by name, until five stacks have been named.
  const shown = Object.entries(stacksBySite)
    .sort(byNameOf(([siteName]) => siteName))
    .reduce<{ siteName: string; stacks: StackNameEntry[] }[]>((acc, [siteName, stacks]) => {
      const room = TOOLTIP_STACK_COUNT - acc.reduce((named, media) => named + media.stacks.length, 0)
      return room > 0 && stacks.length > 0 ? [...acc, { siteName, stacks: [...stacks].sort(byNameOf((stack) => stack.stackName)).slice(0, room) }] : acc
    }, [])
  const hiddenCount = stackCount - shown.reduce((named, media) => named + media.stacks.length, 0)
  const row = 'border-0 border-solid border-slate-grey not-first:pt-1.5 not-last:border-b not-last:pb-1.5'

  return (
    <Tooltip
      title={
        <div>
          {shown.map(({ siteName, stacks }) => (
            <div key={siteName} className={row}>
              <strong>{siteName}: </strong>
              {generateStackNameList(stacks).reduce((acc, stack) => [acc, pipeSeparator, stack])}
            </div>
          ))}
          {hiddenCount > 0 && <div className={row}>{`+ ${hiddenCount} more stack${hiddenCount === 1 ? '' : 's'}`}</div>}
        </div>
      }
      positions={['left', 'bottom', 'top', 'right']}
    >
      {`${stackCount} stack${stackCount > 1 ? 's' : ''}`}
    </Tooltip>
  )
}
